import { CardQuality, GameplayTier } from '#src/__generated__/globalTypes.ts';

import { GAMEPLAY_TIER_LABELS, GAMEPLAY_TIER_ORDER } from './gameplayTiers.ts';
import { qualityNames } from './players.ts';

export type PlayerPoolTier =
  | { quality: CardQuality }
  | { gameplayTier: GameplayTier };

export const QUALITY_POOL_TIERS: PlayerPoolTier[] = Object.values(
  CardQuality
).map(quality => ({ quality }));

export const GAMEPLAY_TIER_POOL_TIERS: PlayerPoolTier[] =
  GAMEPLAY_TIER_ORDER.map(gameplayTier => ({ gameplayTier }));

export const playerPoolTierSlug = (tier: PlayerPoolTier) =>
  'gameplayTier' in tier
    ? tier.gameplayTier.toLowerCase()
    : tier.quality.toLowerCase();

export const playerPoolTierLabel = (tier: PlayerPoolTier) =>
  'gameplayTier' in tier
    ? GAMEPLAY_TIER_LABELS[tier.gameplayTier]
    : qualityNames[tier.quality];

// A U23 league resolves `gameplayTier` against its own pool, so both tier fields map to the same query.
export const cardRewardPoolTier = ({
  quality,
  gameplayTier,
  u23GameplayTier,
}: {
  quality?: CardQuality | null;
  gameplayTier?: GameplayTier | null;
  u23GameplayTier?: GameplayTier | null;
}): PlayerPoolTier | undefined => {
  const tier = gameplayTier || u23GameplayTier;
  if (tier) {
    return { gameplayTier: tier };
  }
  if (quality) {
    return { quality };
  }

  return undefined;
};

export const parsePlayerPoolTierSlug = (
  slug: string
): PlayerPoolTier | undefined => {
  const value = slug.toUpperCase();

  if (value in GameplayTier) {
    return { gameplayTier: value as GameplayTier };
  }
  if (value in CardQuality) {
    return { quality: value as CardQuality };
  }

  return undefined;
};

// `undefined` (not `null`) so Apollo omits them: both fields reject a query carrying two tier arguments.
const gameplayTierVariable = (tier: PlayerPoolTier) =>
  'gameplayTier' in tier ? tier.gameplayTier : undefined;

// `So5League.rewardPool` takes `quality` as a String and looks it up by the pool's lowercase key,
// unlike `cardShardsPool`, which takes the uppercase `CardQuality` enum.
export const leagueRewardPoolVariables = (tier: PlayerPoolTier) => ({
  quality: 'quality' in tier ? tier.quality.toLowerCase() : undefined,
  gameplayTier: gameplayTierVariable(tier),
});

export const craftingPoolVariables = (tier: PlayerPoolTier) => ({
  quality: 'quality' in tier ? tier.quality : undefined,
  gameplayTier: gameplayTierVariable(tier),
});
